import { useMemo } from 'react';
import {
  FormErrors,
  PurchaseOrderRevisionForm,
  UpdatePurchaseOrderRevisionForm,
} from './validation';
import {
  PurchaseOrder,
  PurchaseOrderLine,
} from '../../../Lib/Types/purchase-order';
import { ItemForm } from './ItemForm';

export type ItemsLevelFormProps = {
  purchaseOrder: PurchaseOrder;
  form: PurchaseOrderRevisionForm;
  updateForm: UpdatePurchaseOrderRevisionForm;
  disabled: boolean;
  errors: FormErrors | undefined;
};

function getStyleColorFromSku(sku: string | undefined) {
  if (!sku) {
    return '';
  }
  const [style, color] = sku.split('_') || [];
  return `${style}_${color}`;
}

export function ItemsLevelForm(props: ItemsLevelFormProps) {
  const { purchaseOrder } = props;

  const styles = useMemo(
    () =>
      purchaseOrder.purchase_order_lines.reduce(
        (acc, cur) => {
          const styleColor = getStyleColorFromSku(cur.sku);
          const itemName = cur.item_name.split('-')[0].trim() || '';
          const itemColor = styleColor.split('_')[1].trim() || '';
          const itemNameFormatted = `${itemName} - ${itemColor}`;
          if (!styleColor) return acc;

          const existingStyle = acc.find(
            (style) => style.style_color === styleColor,
          );
          if (existingStyle) {
            existingStyle.items.push(cur);
            return acc;
          }
          return [
            ...acc,
            {
              style_color: styleColor,
              name: itemNameFormatted,
              items: [cur],
            },
          ];
        },
        [] as Array<{
          style_color: string;
          name: string;
          items: PurchaseOrderLine[];
        }>,
      ),
    [purchaseOrder.purchase_order_lines],
  );

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-2">
        <p className="text-lg font-medium text-base-primary">Items</p>
        <p className="text-xsm font-normal text-base-disabled">
          Select the items you&apos;d like to make changes to.
        </p>
      </div>
      {styles.map((style) => (
        <ItemForm
          key={style.style_color}
          styleColor={style.style_color}
          name={style.name}
          lines={style.items}
          {...props}
        />
      ))}
    </div>
  );
}
